import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send,
  Loader2,
  Mail,
  RefreshCcw,
  Check,
  Clock,
  AlertCircle,
  Repeat,
  Trash2,
} from "lucide-react";
import { adminAuthHeader } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => adminAuthHeader();

/**
 * Send a one-off review request to a former patient or caregiver who
 * isn't (yet) in our database. The email's opening line uses the
 * condition the admin types in here.
 *
 * Layout: form on the left, history table on the right. The Studies
 * dropdown was removed in Feb 2026 , the email body no longer
 * references a specific study, only the condition.
 */
export default function SendReviewRequestTab() {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [audience, setAudience] = useState("patient");
  const [condition, setCondition] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoadingHistory(true);
    try {
      const listRes = await axios.get(`${API}/admin/reviews/requests`, {
        headers: getAuthHeaders(),
      });
      setHistory(listRes.data.items || []);
    } catch {
      toast.error("Failed to load review requests.");
    } finally {
      setLoadingHistory(false);
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const resendRequest = async (row) => {
    if (
      !window.confirm(
        `Resend the review request email to ${row.email}?\n\nThis is the second and final chase , after the resend, this entry will drop off your "Sent requests" list.`,
      )
    ) {
      return;
    }
    setResendingId(row.id);
    try {
      await axios.post(
        `${API}/admin/reviews/requests/${row.id}/resend`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(`Resent to ${row.email}.`);
      fetchAll();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't resend the request.";
      toast.error(typeof detail === "string" ? detail : "Couldn't resend the request.");
    } finally {
      setResendingId(null);
    }
  };

  const deleteRequest = async (row) => {
    if (
      !window.confirm(
        `Delete the review request to ${row.email}?\n\nThis removes the row from your list. If the recipient still has the email, the link will no longer work.`,
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    try {
      await axios.delete(`${API}/admin/reviews/requests/${row.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Request deleted.");
      fetchAll();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't delete the request.";
      toast.error(typeof detail === "string" ? detail : "Couldn't delete the request.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !condition.trim()
    ) {
      toast.error("First name, last name, email and condition are all required.");
      return;
    }
    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim().toLowerCase(),
      audience,
      condition: condition.trim(),
    };
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/reviews/request-adhoc`, payload, {
        headers: getAuthHeaders(),
      });
      toast.success(`Email sent to ${payload.email}.`);
      setFirstName("");
      setLastName("");
      setEmail("");
      setAudience("patient");
      setCondition("");
      fetchAll();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't send the request.";
      toast.error(typeof detail === "string" ? detail : "Couldn't send the request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-send-review-request-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
            Send review request
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
            Send a former patient or caregiver a short email asking them to
            leave a review. The condition you type below appears in the
            opening line of the email.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm hover:bg-[var(--bone-soft)]"
        >
          <RefreshCcw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: the form (2/5) */}
        <form
          onSubmit={send}
          className="lg:col-span-2 rounded-2xl border border-[var(--light-border)] bg-white p-5 md:p-6 space-y-5"
          data-testid="ad-hoc-review-request-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldLabel label="First name *">
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Joanna"
                required
                data-testid="ad-hoc-first-name"
              />
            </FieldLabel>
            <FieldLabel label="Last name *">
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Rodriguez"
                required
                data-testid="ad-hoc-last-name"
              />
            </FieldLabel>
          </div>

          <FieldLabel label="Email *">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="joanna@example.com"
              required
              data-testid="ad-hoc-email"
            />
          </FieldLabel>

          <FieldLabel label="They took part as">
            <div className="flex gap-2">
              {[
                { v: "patient", l: "Patient" },
                { v: "caregiver", l: "Caregiver" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setAudience(o.v)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    audience === o.v
                      ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                      : "bg-white text-[var(--charcoal)] border-[var(--light-border)] hover:bg-[var(--bone-soft)]"
                  }`}
                  data-testid={`ad-hoc-audience-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </FieldLabel>

          <FieldLabel label="Condition *">
            <Input
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="e.g. Migraine, Type 2 diabetes, Long COVID"
              required
              data-testid="ad-hoc-condition"
            />
            <p className="text-[12px] text-[var(--text-muted)] mt-1.5 leading-relaxed">
              This appears in the opening of the email: "Thank you again for
              taking part in our recent <em>{condition.trim() || "[condition]"}</em>{" "}
              research."
            </p>
          </FieldLabel>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            data-testid="ad-hoc-send-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={16} />
                Send review request
              </>
            )}
          </button>

          <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
            The email contains two CTAs: <strong>Leave a review</strong>{" "}
            (one-time link to <code>/review/&lt;token&gt;</code>) and{" "}
            <strong>Register with MediTalk</strong> (links to{" "}
            <code>/register</code>). The reviewer's first name is the only
            personal detail shown on a published review.
          </p>
        </form>

        {/* RIGHT: history (3/5) */}
        <div className="lg:col-span-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)] mb-3">
            Sent requests
          </h2>

          {loadingHistory ? (
            <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] py-8">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-2xl border border-[var(--light-border)] bg-white p-8 text-center text-sm text-[var(--text-muted)]">
              No review requests sent yet.
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--light-border)] bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-[13.5px]">
                  <thead className="bg-[var(--bone-soft)] text-left">
                    <tr>
                      <th className="px-4 py-2.5 font-semibold text-[var(--charcoal)]">Person</th>
                      <th className="px-4 py-2.5 font-semibold text-[var(--charcoal)]">Condition</th>
                      <th className="px-4 py-2.5 font-semibold text-[var(--charcoal)]">Sent</th>
                      <th className="px-4 py-2.5 font-semibold text-[var(--charcoal)]">Status</th>
                      <th className="px-4 py-2.5 font-semibold text-[var(--charcoal)] text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--light-border)]">
                    {history.map((r) => (
                      <RequestRow
                        key={r.id}
                        row={r}
                        onResend={resendRequest}
                        onDelete={deleteRequest}
                        resending={resendingId === r.id}
                        deleting={deletingId === r.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const RequestRow = ({ row, onResend, onDelete, resending, deleting }) => {
  const sentAt = row.sent_at ? new Date(row.sent_at) : null;
  const sentLabel = sentAt
    ? sentAt.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

  const canResend = !row.used_at;
  // Anyone in the visible list can be deleted , the row is removed from
  // the DB and the `/review/<token>` link stops working.
  const busy = resending || deleting;

  // The backend stores the condition under `study_title` for backwards
  // compatibility with the older review-request rows, so we keep reading
  // it from there.
  return (
    <tr data-testid={`request-row-${row.id}`}>
      <td className="px-4 py-3 align-top whitespace-nowrap">
        <div className="font-semibold text-[var(--charcoal)]">
          {row.first_name} {row.last_name}
        </div>
        <div className="text-[12px] text-[var(--text-muted)] inline-flex items-center gap-1">
          <Mail size={11} />
          {row.email}
          <span className="inline-block ml-2 px-1.5 py-px rounded-full bg-[var(--bone-soft)] text-[var(--charcoal)] text-[10px] font-semibold uppercase tracking-wider">
            {row.audience === "caregiver" ? "Caregiver" : "Patient"}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 align-top text-[var(--charcoal)] max-w-[220px]">
        {row.study_title ? (
          <span title={row.study_title} className="line-clamp-2">
            {row.study_title}
          </span>
        ) : (
          <span className="text-[var(--text-muted)]">—</span>
        )}
      </td>
      <td className="px-4 py-3 align-top text-[var(--text-muted)] whitespace-nowrap">
        <div>{sentLabel}</div>
        <div className="text-[11px] truncate max-w-[180px]" title={`by ${row.sent_by}`}>
          by {row.sent_by}
        </div>
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap">
        <RequestStatusPill row={row} />
      </td>
      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5">
          {canResend && (
            <button
              type="button"
              onClick={() => onResend(row)}
              disabled={busy}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[var(--light-border)] text-[var(--charcoal)] hover:bg-[var(--bone-soft)] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid={`resend-${row.id}`}
              title="Resend the email (second and final chase)"
              aria-label="Resend"
            >
              {resending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Repeat size={13} />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(row)}
            disabled={busy}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={`delete-${row.id}`}
            title="Delete this request"
            aria-label="Delete"
          >
            {deleting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Trash2 size={13} />
            )}
          </button>
        </div>
      </td>
    </tr>
  );
};

const RequestStatusPill = ({ row }) => {
  if (!row.used_at) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11.5px] font-semibold border border-amber-200">
        <Clock size={11} />
        Awaiting response
      </span>
    );
  }
  const status = row.review_status || "pending";
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[11.5px] font-semibold border border-sky-200">
        <AlertCircle size={11} />
        Awaiting your approval
      </span>
    );
  }
  if (status === "published") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11.5px] font-semibold border border-emerald-200">
        <Check size={11} strokeWidth={3} />
        Published
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11.5px] font-semibold border border-red-200">
      Rejected
    </span>
  );
};

const FieldLabel = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-[var(--charcoal)] mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full h-11 px-3 rounded-lg border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--lime)] focus:ring-2 focus:ring-[var(--lime)]/30"
  />
);
